import { BCMSCurrency, BCMSDefaultModel, BCMSRecord, Branding, ModalOpen, useBCMSModal, useModel, useWorkflowStep } from "@bcm-dev/bcms-vite";
import CustomAmountSelect from "compid/custom-ui/CustomAmountSelect";
import CustomSelect, { CustomSelectItem } from "compid/custom-ui/CustomSelect";
import ButtonContainer from "compid/shared/containers/ButtonContainer";
import PageLayoutContainer from "compid/shared/containers/PageLayoutContainer";
import CenterColumnContainer from "compid/shared/containers/PageLayoutContainer/containers/CenterColumnContainer";
import PageView from "compid/shared/containers/PageViewContainer";
import BillerView from "compid/shared/views/BillerView";
import BillerPaymentView from "compid/shared/views/BillerView/components/views/BillerPaymentView";
import TableView, { TableRowView } from "compid/shared/views/TableView";
import UIButton from "compid/ui/UIButton";
import UILimitedCharacterInput from "compid/ui/UILimitedCharacterInput";
import { getAutopaySubmitMethod, getDefaultPayment, getSubmitMethod, getWorkflowSteps } from "compid/workflows/MakeAPayment";
import { Account, Autopay, Bill, Biller, Payment, ShowOnDesktop, ShowOnMobile } from "model";
import AddedBillerPrompt from "./components/AddedBillerPrompt";

import NonEBillForm from "compid/shared/views/AutopayFormView/components/NonEBillForm";
import { DatePickerView } from "compid/ui/DatePicker";
import UIToggle from "compid/ui/UIToggle";
import { SVGAsset } from "res";
import BillDueNotificationView from "./components/BillDueNotificationView";
import RepeatPaymentPrompt from "./components/RepeatPaymentPrompt";
import "./styles.css";

export default function ReviewAndPayPage(props) {
    const workflowStep = useWorkflowStep(getDefaultPayment, getWorkflowSteps);
    BCMSRecord.globalControllerFor([ Account, Biller, Bill, Payment, Autopay ], workflowStep.globalController);

    const { model } = workflowStep ?? {};
    const email = model.emails[0].emailName;

    const payment = workflowStep.read("payment");
    const bill = workflowStep.read("bill");
    const isRepeatPayment = workflowStep.read("repeatPayment") ?? false;

    const biller = payment.to;
    const billDue = bill.status.toLowerCase() === "due" ? true : false;
    const eBillActive = biller.eBillStatus.toLowerCase() === "active" ? true : false;

    const billerHasAutopays = Autopay.getByBillerID(biller.id).length > 0;
    const showAutopayForm = !(biller.isActiveEBiller && !biller.hasEbillAutopay && !billerHasAutopays) &&
        !(biller.isActiveEBiller && !biller.hasEBillAutopay && billerHasAutopays);

    const amount = payment.amount.format();
    const { memo } = payment;

    const accountNumber = payment.from.number;
    const accountID = payment.from.id;
    const dueDate = bill.status.toLowerCase() === "due" ? bill.date : undefined;

    const updateAmount = value => workflowStep.update(["payment", "amount"], value.replace("$", ""));
    const updateMemo = value => workflowStep.update(["payment", "memo"], value);
    const updateDate = value => workflowStep.update(["payment", "date"], value);
    const updateFromAccount = value => workflowStep.update(["payment", "from"], value);
    const updateRepeatPayment = value => workflowStep.update([ "repeatPayment" ], value);
    const setFeeDate = evt => workflowStep.update([ "payment", "date" ], Payment.getFeeDate(payment.to.paymentType));

    const isPaper = payment.to.paymentType === "paper";
    const isElectronic = payment.to.paymentType === "electronic";
    const isVirtualCard = payment.to.paymentType === "virtualCard";
    const isInstantPayment = payment.to.paymentType === "instantPayment";
    const isWithinMinutes = payment.to.paymentType === "withinMinutes";

    const { branding } = workflowStep.model;
    // This section determines if a date with a fee is selected and if the branding should show fees
    const options = workflowStep.model.branding.options;
    const hasRealTimeGoodFunds = Payment.hasRealTimeGoodFunds(payment.to.paymentType, branding);

    const feeIsSelected = Payment.getIfDateHasFee(payment.date, payment.to.paymentType, workflowStep.model.branding);

    let showFeeLabel = isPaper ? options.overnight_check_rxp22 : options.same_day_payment_rxp22,
        feeLabel = "";

    showFeeLabel = (isInstantPayment || isWithinMinutes) ? options.instant_payments_rxp22 : showFeeLabel;
    
    if (showFeeLabel === "true") { feeLabel = feeIsSelected ? "Rush delivery selected" : "Rush delivery available"; }
    if ((hasRealTimeGoodFunds) || isVirtualCard || isInstantPayment || isWithinMinutes) { feeLabel = ""; }

    const instantPaymentFee = options.same_day_ip_fee_rxp22 === "Free" || options.same_day_ip_fee_rxp22 === "undefined"
        ? "Free" 
        : BCMSCurrency.init(options.same_day_ip_fee_rxp22.replace(/[$]/g, "")).format();

    const withinMinutesFee = options.within_minutes_ip_fee_rxp22 === "Free" || options.within_minutes_ip_fee_rxp22 === "undefined"
        ? "Free"
        : BCMSCurrency.init(options.within_minutes_ip_fee_rxp22.replace(/[$]/g, "")).format();

    // Determine if there are any recent activity for biller
    const recentActivity = workflowStep.model.payments.map(payment => Payment.init(payment), [])
        .filter(payment => payment.to.id === bill.biller.id, [])
        .sort((a, b) => {
            if(a.date.isBefore(b.date)) { return 1; }
            else if(b.date.isBefore(a.date)) { return -1; }
            else { return 0; }
        });
    const scheduledPayment = recentActivity.find(payment => payment.status === "scheduled");
    const processedPayment = recentActivity.find(payment => payment.status === "processed");

    var status = bill.status === "paid" ? bill.payment.status : bill.status;
    let amountSubText = "";
    var dateLabel = bill.formatDate;
    var amountLabel = bill.amount;
    var showPaymentInfo = true;
    var description = "";
    const billStatus = bill.status.toLowerCase();

    // There is an amount sub text disclaimer for eletronic billers with active "eBill". 
    // A class is conditionally added for that disclaimer.
    const amountSubTextForElectronicEBiller = (isElectronic && eBillActive) ? { amountSubText: `These payment amount descriptions may differ from your eBill. Your eBill is the definitive record of your account.` } : {};
    const hasAmountSubTextForElectronicEBiller = Object.keys(amountSubTextForElectronicEBiller).length > 0;
    const classForAmountSubTextForElectronicEBiller = hasAmountSubTextForElectronicEBiller ? "amount-sub-text-electronic-ebiller" : "";

    const showInfoTextRight = hasRealTimeGoodFunds && (isElectronic || isInstantPayment || isWithinMinutes);
    const infoTextRight = showInfoTextRight 
        ? <div className="date-helper-text">
                <span className="sub-text payment-type"><i>Electronic</i></span>
            </div>
        : <></>

    // START: This biller/last payment messaging was written to match what we see in "Pay Bills".
    const { payments } = useModel();
    const lastPayment = Payment.getLastPaymentByBillerID(bill.biller.id, payments);
    const showFees = isVirtualCard ? "false" : !hasRealTimeGoodFunds ? "true": "false";
    
    if(bill.status.toLowerCase() !== "due" && lastPayment) {
        description = <><span className="show-on-desktop">Last payment</span><span className="show-on-tablet">Last pmt</span></>;
        amountLabel = lastPayment.amount;
        status = lastPayment.status;
        dateLabel = lastPayment.formatDate;
    } else if (bill.status.toLowerCase() !== "due" && !lastPayment) {
        showPaymentInfo = false;
    }

    if(billStatus === "canceled") {
        showPaymentInfo = false;
    }

    // Create sub text if the biller has a scheduled or processed payment
    if(recentActivity.length > 0 && scheduledPayment) {
        amountSubText = scheduledPayment instanceof Payment
            ? `Next Payment: ${ scheduledPayment.amount.format() } on ${ scheduledPayment.formatDate }`
            : `Last Payment: ${ processedPayment.amount.format() } on ${ processedPayment.formatDate }`
    }

    const getFee =  () => {
        if(feeIsSelected) {
            let amount = isPaper
                ? BCMSCurrency.init(options.overnight_check_fee_rxp22 === "undefined" ? "14.95" : options.overnight_check_fee_rxp22).amount
                : BCMSCurrency.init(options.same_day_fee_rxp22 === "undefined" ? "9.95" : options.same_day_fee_rxp22).amount;

            if (isInstantPayment) { amount = BCMSCurrency.init(options.same_day_ip_fee_rxp22 === "Free" ? "0.00" : options.same_day_ip_fee_rxp22).amount; }
            if (isWithinMinutes) { amount = BCMSCurrency.init(options.within_minutes_ip_fee_rxp22 === "Free" ? "0.00" : options.within_minutes_ip_fee_rxp22).amount; }
        
            return amount;
        }

        return 0;
    }

    const customSelectItems = Account.initAll().map((account) => (<CustomSelectItem key={ account.id } value={ account.id }>{ account.name + " " + account.number }</CustomSelectItem>));
    // "Leave" button is clicked in the "Are you sure you want to leave?" modal.
    const onLeave = () => workflowStep.navigate("/paybills");

    // START: Create the "Withdrawn on..." message. 
    let withdrawnOnDateLabel = "",
        withdrawnOnDate = Payment.getWithdrawOnDate(payment.date, payment.to.paymentType, branding);

    payment.withdrawnOnDate = withdrawnOnDate;
    withdrawnOnDateLabel = withdrawnOnDate.format("M d");

    let withdrawnLabel = hasRealTimeGoodFunds ? <p>Withdrawn on { withdrawnOnDateLabel }</p> : undefined;
    withdrawnLabel = (isInstantPayment || isWithinMinutes) && feeIsSelected ? <p>Withdraw immediately</p> : withdrawnLabel;
    // END: Create the "Withdrawn on..." message. 

    const goToStep = options.overnight_check_rxp22 === "true" && options.paper_biller_rush_delivery_rxp22 === "Overnight Check" && feeIsSelected && isPaper
        ? 3 : undefined;

    const submitPayment = getSubmitMethod(workflowStep, goToStep);
    const onSubmitAutopay = getAutopaySubmitMethod(workflowStep, goToStep);

    const availableBalanceText = `Available balance: ${ payment.from.balance.format() }`;

    let fundsMessage1 = "",
        fundsMessage2 = "",
        hasImmediateWithdrawDate = false;

    if(hasRealTimeGoodFunds) {
        if(withdrawnOnDate.isToday()) {
            fundsMessage1 = `The funds will be withdrawn from your account immediately.`;
            hasImmediateWithdrawDate = true;
    
            if(payment.date.isToday()) {
                fundsMessage1 = `The funds will be withdrawn from your account ${ accountNumber } immediately`;
                fundsMessage2 = `Once the payment is submitted it cannot be modified or canceled.`;
            }
    
        } else {
            fundsMessage1 = `If funds are not available on ${ withdrawnOnDate.format("M d") }, the payment will fail.`;
        }
    }

    const paperBillerDatePickerSubtext = hasRealTimeGoodFunds
        ?   withdrawnLabel
        :   <div className="info-text-container flex-container flex-horizontal align-top">
                <SVGAsset id="UpsellIcon" className="icon info-icon blue-icon set-width" />
                <p className="sub-text">The timing of the withdrawal depends on when the check is cashed. This may happen on, before, or after { payment.formatDate }.</p>
            </div>;

    return (
        <PageView className="review-and-pay-page">
            <p className="page-sub-header semi-bold-text">REVIEW AND PAY</p>

            <PageLayoutContainer>
                <div className="flex-container flex-vertical flex-width">
                    <div className="flex-height flex-container flex-vertical upper-content-container">
                        <AddedBillerPrompt />
                        
                        { eBillActive && billDue && <BillDueNotificationView bill={ bill } /> }
                        { !showAutopayForm && <RepeatPaymentPrompt billerID={ biller.id } /> }

                        <BillerView 
                            biller={ payment.to } 
                            bill={ bill }
                            dataPoint="Review_And_Pay_Biller"
                            paymentInfo={
                                <BillerPaymentView description={ description } amount={ amountLabel } payment={ payment }
                                status={ status } date={ dateLabel } biller={ biller } dataPoint={ "Review_And_Pay_Page" }/>
                            }
                            showPaymentInfo={ showPaymentInfo }
                             />
                    </div>
                    
                    <div className="flex-height flex-width flex-container all-center">
                        <CenterColumnContainer>
                            <div className="form-group flex-container flex-vertical">
                                <div className={ `amount-input-container form-row ${ classForAmountSubTextForElectronicEBiller }` }>
                                    <CustomAmountSelect className="amount-input" bill={ bill } helpText={ amountSubText } { ...amountSubTextForElectronicEBiller } value={ amount } placeHolder="Amount" onChange={ updateAmount } dataPoint="Review_And_Pay_Amount"/>
                                        
                                    {/* remove chevron for non eBillers */}
                                    {/* { hasEBills && <SVGAsset id="DownChevronIcon" className="amount-input-chevron blue-icon" /> } */}
                                </div>
                                
                                { isPaper && <div className="form-row flex-height">
                                        <DatePickerView dataPoint="Review_Pay_Date" setDate={ payment.date } dateLabel="Estimated delivery" rightLabel={ feeLabel } onChange={ updateDate } showFees={ showFees } billerType={ payment.to.paymentType } dueDate={ dueDate } infoClasses="flex-width">
                                            <div className="flex-container flex-horizontal align-left">
                                                { paperBillerDatePickerSubtext }
                                            </div>
                                        </DatePickerView>
                                    </div> }

                                { !isPaper && <div className="form-row flex-height">
                                        <DatePickerView setDate={ payment.date } dateLabel="Deliver by" rightLabel={ feeLabel } onChange={ updateDate } infoClasses="flex-width"
                                        showFees={ showFees } billerType={ payment.to.paymentType } dueDate={ dueDate } dataPoint="Review_And_Pay" infoTextRight={ infoTextRight }>
                                            <div className="flex-container flex-horizontal align-center date-helper-text">
                                                <span className="sub-text">{ withdrawnLabel }</span>
                                            </div>

                                            <Branding id="instant_payments_rxp22" value="true" default="false">
                                                <div className="flex-container flex-horizontal align-center date-helper-text">
                                                    { (isInstantPayment && !feeIsSelected) && <a onClick={ setFeeDate }>Get payment delivered today for { instantPaymentFee }</a> }
                                                    { (isInstantPayment && feeIsSelected) && <span className="sub-text">Payment will be delivered today</span> }

                                                    { (isWithinMinutes && !feeIsSelected) && <a onClick={ setFeeDate }>Get payment delivered in minutes for { withinMinutesFee }</a> }
                                                    { (isWithinMinutes && feeIsSelected) && <span className="sub-text">Payment will be delivered in minutes</span> }
                                                </div>
                                            </Branding>
                                        </DatePickerView> 
                                    </div> }

                                <div className="form-row flex-height hide-dropdown-arrow">
                                    <CustomSelect label="Pay from" helpText={""} value={ accountID } onChange={ updateFromAccount } dataPoint="Review_And_Pay_Account">
                                        { customSelectItems }
                                    </CustomSelect>
                                    
                                    { hasRealTimeGoodFunds &&
                                        <>
                                            <div className="flex-container flex-horizontal align-center available-balance gray-text">
                                                <span className="sub-text">{ availableBalanceText }</span>
                                            </div>
                                            <div className="flex-container flex-horizontal align-center select-lower-text">
                                                <span className="sub-text">{ fundsMessage1 }</span>
                                            </div>
                                            <div className="flex-container flex-horizontal align-center select-lower-text">
                                                <span className="sub-text">{ fundsMessage2 }</span>
                                            </div>
                                        </>
                                    }
                                </div>
                                
                                <TableView label="Label" data="Data" />

                                { isPaper && <UILimitedCharacterInput placeHolder="Memo" value={ memo } onChange={ updateMemo } className="memo-input" dataPoint="Review_And_Pay_Memo" /> }
                            
                                { isVirtualCard &&
                                    <div className="form-row flex-height">
                                        <div className="flex-container flex-horizontal align-center">
                                            <span className="sub-text">{ Biller.getVirtualCardDisclaimer(true) }</span>
                                        </div>
                                    </div>
                                }

                                { showAutopayForm && 
                                    <div className="form-row flex-height">
                                        <div className="flex-container flex-horizontal">
                                            <ShowOnDesktop>
                                                <span className="flex-width"><b>Make this a repeat payment</b></span>
                                            </ShowOnDesktop>

                                            <ShowOnMobile>
                                                <span className="flex-width"><b>Repeat Payment</b></span>
                                            </ShowOnMobile>

                                            <UIToggle text="" value={ isRepeatPayment } onChange={ updateRepeatPayment } />
                                        </div>
                                    </div> }

                                { isRepeatPayment &&
                                    <>
                                        <NonEBillForm workflowStep={ workflowStep } />

                                        <div className="email-notifications-container">
                                            <p>Send email notifications to <b>{ email }</b> when the payment:</p>

                                            <label className="flex-container flex-horizontal align-center space-between is-due clickable">
                                                <p>Is due</p>
                                                <UIToggle text="" />
                                            </label>

                                            <label className="flex-container flex-horizontal align-center space-between has-been-sent clickable">
                                                <p>Has been sent</p>
                                                <UIToggle text="" />
                                            </label>
                                        </div>
                                    </> }
                            </div>

                            { feeIsSelected &&
                                <div className="fee-table flex-container justify-right">
                                    <TableView>
                                        <TableRowView label="Delivery Fee" dataClass="text-right" data={BCMSCurrency.init(getFee()).format() } />
                                    </TableView>
                                </div> }
                           
                            
                            <ReviewAndPayButtonContainer 
                                getFee={ getFee } 
                                workflowStep={ workflowStep }
                                hasRealTimeGoodFunds={ hasRealTimeGoodFunds }
                                onLeave={ onLeave } 
                                payment={ payment } 
                                isRepeatPayment={ isRepeatPayment }
                                feeIsSelected={ feeIsSelected }
                                submitAutopay={ onSubmitAutopay }
                                submitPayment={ submitPayment }/>


                            
                            {/*<ButtonContainer className="form-button-container">
                                <UIButton className="secondary-button">
                                    <ModalOpen id="WantToLeaveModal" data={ {onLeave: onLeave} }>Cancel</ModalOpen>
                                </UIButton>
                                <UIButton className="primary-button" onClick={ submitPayment } dataPoint="Review_And_Pay_Pay_Button">Pay {  BCMSCurrency.init(parseFloat(payment.amount.amount) + parseFloat(getFee())).format() }</UIButton>
                            </ButtonContainer>*/}
                        </CenterColumnContainer>
                    </div>
                </div>
            </PageLayoutContainer>
        </PageView>
    );
}

function ReviewAndPayButtonContainer(props) {
    const payment = props.payment;
    const isRepeatPayment = props.isRepeatPayment;
    const onLeave = props.onLeave;
    
    const submitPayment = props.submitPayment;
    const submitAutopay = props.submitAutopay;

    const workflowStep = props.workflowStep ?? {};
    const feeIsSelected = props.feeIsSelected ?? false;

    const getFee = props.getFee;
    const hasRealTimeGoodFunds = props.hasRealTimeGoodFunds;
    const defaultPayment = BCMSDefaultModel.payment();
    let paymentAmount = parseFloat(payment.amount.amount);
    const defaultPaymentAmount = parseFloat(defaultPayment.amount.amount);
    paymentAmount =  paymentAmount === 0 ? defaultPaymentAmount : paymentAmount;
    const payFromAccount = payment.from;
    const payFromAccountAccountBalance = parseFloat(payFromAccount.balance.amount);
    
    const { open } = useBCMSModal();
    const { branding } = workflowStep.model ?? {};
    const { options } = branding ?? {};

    const showOvernightDelivery = options.overnight_check_rxp22 === "true" && options.paper_biller_rush_delivery_rxp22 === "Overnight Check" 
        && feeIsSelected && payment.to.paymentType === "paper";

    // A modal may open depending on branding.
    const onAddAutopayClick = () => {
        // to do: check if eBills are set since rtgf branding may not apply for that.
        if(hasRealTimeGoodFunds && !showOvernightDelivery) {
            if(paymentAmount > payFromAccountAccountBalance) {
                open("InsufficientFundsModal", { payAmount: payment.amount, payFrom: payFromAccount, onModalPayButtonClick: submitPayment });
            } else {
                submitPayment();
            }
        } else {
            submitPayment();
        }
    }

    return (
    <ButtonContainer className="form-button-container">
        <UIButton className="secondary-button">
            <ModalOpen id="WantToLeaveModal" data={ { onLeave: onLeave } }>Cancel</ModalOpen>
        </UIButton>

        { !isRepeatPayment && <UIButton className="primary-button" onClick={ onAddAutopayClick } dataPoint="Review_And_Pay_Pay_Button">Pay {  BCMSCurrency.init(parseFloat(payment.amount.amount) + parseFloat(getFee())).format() }</UIButton> }
        { isRepeatPayment && <UIButton className="primary-button" dataPoint="Review_And_Pay_Pay_Button" onClick={ submitAutopay }>Add Autopay</UIButton >}
    </ButtonContainer>
    );
}